<script>
import partnerNames from '@/mixins/partnerNames';
import {hasEditPermission, currentRoleLevelName} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';
import { agreementMajorityAge, childrenUnderMajority } from '@/lib/agreement/filters';

export default {
	mixins: [partnerNames],
	props: ['agreementData'],
	data(){
		return{
			isViewMode : !hasEditPermission('section-parenting'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		}
	},
	computed: {
		...mapState(['currentRoleLevel']),
		guardianship() {
			return this.agreementData.guardianship
			//  =  this.agreementData.guardianship.hasOwnProperty('additionalClausesToRecitals') 
			// 	? this.agreementData.guardianship 
			// 	: {...this.agreementData.guardianship, additionalClausesToRecitals: []};
		},
		parenting() {
			return this.agreementData.parenting;
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					choice: 'What type of guardianship will you have?',
					commenced: "When does sole guardianship commence?",
					emergencyCommunication: "Will you each keep each other informed of emergency situations that occur during parenting?",
					keepEachPartyInformed:"Will you each keep each other informed about significant matters that occur during parenting time?",
					specifyCommunicationMethods:"Do you want to specify how you will keep each other informed?",
					communicationMethods:"How will you keep each other informed?",
					simpleDecision:"If you did not engage in creating a process who makes decisions?",
					disputeResolutionUnspecified:"What will you do if they cannot agree?",
					responseTime: "Upon receiving a decision-making request, within what timeframe should the other parent respond?",
					disputeResolution: "What will you do if they cannot agree?",
					disputeResolutionPartner : "Defer to which parent to make the final decision?",
					disputeResolutionUnspecifiedPartner:"Defer to which parent to make the final decision?"
				}
			}
			else{
				return {
					choice: 'What type of Guardianship do the parties agree to?',
					commenced: "Does sole guardianship commence on a specific date, or when this agreement is signed?",
					emergencyCommunication: "Will the parties keep each other informed of emergency situations that occur during parenting time?",
					keepEachPartyInformed:"Will the parties keep each other informed of significant matters that occur during parenting time?",
					specifyCommunicationMethods:"Do the parties want to specify how they will keep each other informed?",
					communicationMethods:"How will the parties keep each other informed?",
					simpleDecision:"If the parties did not engage in creating a process who makes decisions?",
					disputeResolutionUnspecified:"What will the parties do if they cannot agree?",
					responseTime: "Upon receiving a decision-making request, within what timeframe should the receiving party respond?",
					disputeResolution: "What will the parties do if they cannot agree?",
					disputeResolutionPartner : "Defer to which party to make the final decision?",
					disputeResolutionUnspecifiedPartner:"Defer to which party to make the final decision?"
				}
			}
		},
		majorityAge(){
			return agreementMajorityAge(this.agreementData);
		},
		childrenUnderMajority(){
			return childrenUnderMajority(this.agreementData.children, this.majorityAge);
		}
	},
	mounted(){
		this.agreementData.guardianship =  this.agreementData.guardianship.hasOwnProperty('additionalClausesToRecitals') ? this.agreementData.guardianship 
		: {...this.agreementData.guardianship, additionalClausesToRecitals: []};
	}
};
</script>

<style scoped>
	.warning-link-a{
		color: #856404;
	}
</style>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Parenting Responsibilities</h4>
			<a href="#" onclick="Intercom('showArticle', 11106525)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>
		<b-alert variant="warning" :show="!childrenUnderMajority.length">
			<h5 class="alert-heading">Warning!</h5>
			<div>
				Parenting provisions in a Separation Agreement only apply to children UNDER {{ majorityAge }} years old.
				<div>Please ensure you have entered the correct birthdate for the relevant child. Or return to the <a href="#matter-details" class="warning-link-a"><b>Matter Details</b></a> section and deselect <b>Parenting</b>.</div>
			</div>
		</b-alert>		
		<yes-no-choice
			v-model="parenting.coParentingPrinciplesIncluded"
			reset-undefined
			class="mb-3"
			label="Would you like to include your Co-Parenting Principles as a schedule to the Agreement?"
			:testid="'co-parenting-Principles-as-schedule-to-agreement'"
			:disabled="isViewMode"
		/>
		<radio-group
			v-model="guardianship.choice"
			select
			input-cols="4"
			class="mb-3"
			label="How will parenting responsibilities be structured?"
			:options="['Sole', 'Joint']"
			:testid="'parenting-responsibility-guardianship-type'"
			:disabled="isViewMode"
			toolTipText="Guardianship refers to the legal authority to make significant decisions about a child's upbringing.
			It involves having the right to make important decisions regarding the child's health, education, religion, and extracurricular activities.
			A person is automatically a child's guardian if they are the child's birth parent, adoptive parent,
			or if they have become the child's guardian through a court order or written agreement.
			In nearly all separation and divorce situations, a parent who has lived with with a child will be a guardian and 'joint guardianship' should be selected.
			If you are thinking about sole guardianship, please obtain legal advice about this before signing your Agreement."
		/>
		<conditional-group
			class="mb-3"
			:condition="guardianship.choice === 'Sole'"
		>
			<radio-group
				v-model="guardianship.guardian"
				reset-undefined
				select
				input-cols="4"
				class="mb-3"
				partner
				label="Who will be the sole guardian?"
				:agreement-data="agreementData"
				:testid="'parenting-responsibility-sole-guardian'"
				:length="'long'"
				:disabled="isViewMode"
				toolTipText="Who will have sole authority and responsibility for making significant decisions about a child's upbringing?"
			/>
			<radio-group
				v-model="guardianship.commenced"
				reset-undefined
				select
				input-cols="6"
				:label="dynamicQuestions.commenced"
				:options="['Specific Date', 'When this Agreement is signed']"
				:testid="'parenting-responsibility-sole-guardianship-commencement'"
				:length="'long'"
				:disabled="isViewMode"
				toolTipText="On a particular date or, more commonly, when the Agreement is signed."
			/>
			<conditional-group
				class="mt-3"
				:condition="guardianship.commenced === 'Specific Date'"
			>
				<date-input
					v-model="guardianship.commencedDate"
					reset-undefined
					label="Date sole guardianship commenced"
					:testid="'parenting-responsibility-sole-guardianship-specific-date'"
					:disabled="isViewMode"
				/>
			</conditional-group>
		</conditional-group>
		<conditional-group :condition="guardianship.choice === 'Joint'">
			<yes-no-choice
				v-model="parenting.emergencyCommunication"
				reset-undefined
				class="mb-3"
				:label="dynamicQuestions.emergencyCommunication"
				:testid="'parenting-responsibility-emergency-situation-informed'"
				:disabled="isViewMode"
				toolTipText="Typically, the person who is with a child will make an emergency decision related to a child and then inform the other parent as soon as reasonably possible.
				For more information, see the materials in the Knowledge Base in this section."
			/>
			<yes-no-choice
				v-model="parenting.keepEachPartyInformed"
				reset-undefined
				class="mb-3"
				:label="dynamicQuestions.keepEachPartyInformed"
				:testid="'parenting-responsibility-significant-matters-informed'"
				:disabled="isViewMode"
				toolTipText="Typically, the person who is with a child will make day-to-day decisions and only inform the other parent of matters of significance . For more information, see the materials in the Knowledge Base in this section."
			/>
			<conditional-group
				:condition="parenting.keepEachPartyInformed == true"
			>
				<yes-no-choice
					v-model="parenting.specifyCommunicationMethods"
					reset-undefined
					class="mb-3"
					:label="dynamicQuestions.specifyCommunicationMethods"
					:testid="'parenting-responsibility-is-communication-methods'"
					:disabled="isViewMode"
					toolTipText="It is wise to set boundaries and expectations around how you will keep each other informed.
					As you can see in the Parenting Communication Video in the Knowledge Base,
					using a joint calendar can help facilitate 75% of informative communication and supplementing that with another written form of communication is most common."
				/>
				<conditional-group
					class="mt-3"
					:condition="parenting.specifyCommunicationMethods"
				>
					<check-group
						v-model="parenting.communicationMethods"
						other
						:options="[
							{ text: 'Text Message', value: 'text message' }, // value fix harder
							{ text: 'Email Message', value: 'email message' },
							{
								text: 'Exchanging a communication journal',
								value: 'exchanging a communication journal',
							},
							{
								text: 'Co-parenting app',
								value: 'communicating through a co-parenting app',
							},
							{ text: 'Calendar', value: 'calendar' },
						]"
						:label="dynamicQuestions.communicationMethods"
						@reset="parenting.communicationMethods = []"
						:testid="'parenting-responsibility-communication-methods'"
						:id="'how-parties-keep-informed'"
						:disabled="isViewMode"
					/>
					<conditional-group
						class="mt-3"
						:condition="
							parenting.communicationMethods &&
							parenting.communicationMethods.includes('Calendar')
						"
					>
						<yes-no-choice
							v-model="parenting.electronicCalendar"
							reset-undefined
							label="Will the calendar be electronic?"
							:testid="'parenting-responsibility-electronic-calender'"
							:disabled="isViewMode"
						/>
					</conditional-group>
				</conditional-group>
			</conditional-group>
			<radio-group
				v-model="parenting.decisionMakingProcess"
				reset-undefined
				class="my-3"
				select
				input-cols="8"
				label="Do you want to include a simple joint decision-making clause, or outline a decision-making process?"
				:options="[
					{
						text: 'Joint decision-making with no specified process',
						value: 'joint-decision-making',
					},
					{
						text: 'Decision-making Process',
						value: 'decision-making-process',
					},
				]"
				:testid="'parenting-responsibility-decision-making-clause'"
				:length="'long'"
				:disabled="isViewMode"
				toolTipText="A simple decision-making clause outlines WHAT you have to decide
				together and a decision-making process extends that idea to include HOW you will
				actually communicate the important issues around this. It is wise to include the
				process in your agreement because it helps set new expectations on
				communication. For more information, see the Parenting Communication Video in
				the Knowledge Base in this section."
			/>
			<conditional-group
				class="mt-3"
				:condition="
					parenting.decisionMakingProcess ===
					'decision-making-process'
				"
			>
				<radio-group
					v-model="parenting.responseTime"
					reset-undefined
					class="mb-3"
					input-cols="4"
					select
					other
					:label="dynamicQuestions.responseTime"
					:options="[
						{ text: '3 hours', value: '3-hours' },
						{ text: '6 hours', value: '6-hours' },
						{ text: '24 hours', value: '24-hours' },
						{ text: '48 hours', value: '48-hours' },
						{ text: 'One week', value: '1-week' },
						{
							text: 'No specific timeframe',
							value: 'no-timeframe',
						},
					]"
					:testid="'parenting-responsibility-receiving-party-respond-timeframe'"
					:length="'long'"
					:disabled="isViewMode"
				/>
				<radio-group
					v-model="parenting.disputeResolution"
					reset-undefined
					class="mb-3"
					input-cols="6"
					select
					:label="dynamicQuestions.disputeResolution"
					:options="[
						{ text: 'Attend mediation', value: 'attend mediation' },
						{
							text: 'Attend a mediation/arbitration process',
							value: 'attend a mediation/arbitration process', // value fix
						},
						{
							text: 'Consult co-parenting coaches',
							value: 'consult co-parenting coaches',
						},
						{
							text: 'Consult divorce coaches',
							value: 'consult divorce coaches',
						},
						{
							text: 'Consult a child specialist',
							value: 'consult a child specialist',
						},
						{
							text: 'Contact a parenting coordinator',
							value: 'contact a parenting coordinator',
						},
						{ text: 'Defer to one party', value: 'defer' },
					]"
					:testid="'parenting-responsibility-dispute-resolution'"
					:length="'long'"
					:disabled="isViewMode"
				/>
				<conditional-group
					class="mt-3"
					:condition="parenting.disputeResolution === 'defer'"
				>
					<radio-group
						v-model="parenting.disputeResolutionPartner"
						reset-undefined
						:agreement-data="agreementData"
						input-cols="4"
						select
						partner
						:label="dynamicQuestions.disputeResolutionPartner"
						:testid="'parenting-responsibility-defer-resolution'"
						:length="'long'"
						:disabled="isViewMode"
					/>
				</conditional-group>
			</conditional-group>
			<conditional-group
				class="mt-3"
				:condition="
					parenting.decisionMakingProcess === 'joint-decision-making'
				"
			>
				<check-group
					v-model="parenting.jointDecisions"
					label="Which parenting decisions should be listed in the Agreement?"
					class="my-3"
					:options="[
						{
							text: 'Healthcare (non-emergency)',
							value: 'healthcare',
						},
						{ text: 'Education', value: 'education' },
						{
							text: 'Social environment',
							value: 'social-environment',
						},
						{
							text: 'Extracurricular activities',
							value: 'extracurriculars',
						},
						{
							text: 'Matters of upbringing related to culture, religion, or linguistics',
							value: 'culture',
						},
						{ text: 'Parenting time', value: 'parenting' },
						{
							text: 'Third party care choices',
							value: 'third-party-care',
						},
					]"
					:testid="'parenting-responsibility-parenting-decision-listing'"
					:id="'which-parenting-decisions-informed'"
					:disabled="isViewMode"
				></check-group>
				<radio-group
					v-model="parenting.simpleDecision"
					reset-undefined
					:agreement-data="agreementData"
					input-cols="4"
					class="mb-3"
					select
					partner
					:options="['Jointly']"
					:label="dynamicQuestions.simpleDecision"
					:testid="'parenting-responsibility-decision-maker'"
					:length="'long'"
					:disabled="isViewMode"
				/>
				<conditional-group
					class="mt-3"
					:condition="parenting.simpleDecision === 'Jointly'"
				>
					<radio-group
						v-model="parenting.disputeResolutionUnspecified"
						reset-undefined
						:agreement-data="agreementData"
						input-cols="4"
						select
						:label="dynamicQuestions.disputeResolutionUnspecified"
						:options="[
							{
								text: 'Attend mediation',
								value: 'attend mediation', // value fix
							},
							{
								text: 'Attend a mediation/arbitration process',
								value: 'attend a mediation/arbitration process',
							},
							{
								text: 'Consult co-parenting coaches',
								value: 'consult co-parenting coaches',
							},
							{
								text: 'Consult divorce coaches',
								value: 'consult divorce coaches',
							},
							{
								text: 'Consult a child specialist',
								value: 'consult a child specialist',
							},
							{
								text: 'Contract a parenting coordinator',
								value: 'contract a parenting coordinator',
							},
							{ text: 'Defer to one party', value: 'defer' },
						]"
						:testid="'parenting-responsibility-dispute-resolution-unspecified'"
						:length="'long'"
						:disabled="isViewMode"
					/>
					<conditional-group
						class="mt-3"
						:condition="
							parenting.disputeResolutionUnspecified === 'defer'
						"
					>
						<radio-group
							v-model="
								parenting.disputeResolutionUnspecifiedPartner
							"
							reset-undefined
							:agreement-data="agreementData"
							input-cols="4"
							select
							partner
							:label="dynamicQuestions.disputeResolutionUnspecifiedPartner"
							:testid="'parenting-responsibility-defer-final-decision-maker'"
							:length="'long'"
							:disabled="isViewMode"
						/>
					</conditional-group>
				</conditional-group>
			</conditional-group>
		</conditional-group>
		<additional-clauses :title="'Additional Clauses into Recitals'" :newLabel="'Add Clause to Recitals'" class="mt-2" v-model="guardianship.additionalClausesToRecitals" :testid="'parenting-responsibility-add-clause-to-recitals-'" 
		:disabled="isViewMode"/>
		<additional-clauses :title="'Additional Clauses into Sections'" :newLabel="'Add Clause to Section'" class="mt-2" v-model="guardianship.additionalClauses" :testid="'parenting-responsibility-add-clause-'"
		:disabled="isViewMode"/>
	</b-card>
</template>
